import {
  ChangeDetectionStrategy, 
  Component,
  Input,
  forwardRef,
  OnInit,
  OnDestroy,
  EventEmitter,
  Output,
  OnChanges,
  SimpleChanges
} from '@angular/core';

import {
  ControlValueAccessor,
  NG_VALUE_ACCESSOR,
  NG_VALIDATORS,
  Validator,
  AbstractControl,
  ValidationErrors
} from '@angular/forms';

import { Subscription } from 'rxjs';
import { SharedDataService } from '../../all-services';

@Component({
  selector: 'paf-combobox',
  templateUrl: './paf-combobox.component.html',
  styleUrls: ['./paf-combobox.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => PafComboBoxComponent),
      multi: true
    },
    {
      provide: NG_VALIDATORS,
      useExisting: forwardRef(() => PafComboBoxComponent),
      multi: true
    }
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PafComboBoxComponent<T extends { Id?: number }>
  implements OnInit, OnDestroy, OnChanges, ControlValueAccessor, Validator {

  /** INPUTS */
  @Input() data: T[] = [];
  @Input() required = false;
  @Input() disabled = false;
  @Input() headerPafLabel?: string;
  @Input() bindToId?: boolean = false;

  /**
   * Optionnel : fonction custom pour formater l’affichage
   */
  @Input() displayFn?: (item: T) => string;

  /**
   * OUTPUT : l’objet sélectionné (le modèle exposé aux parents)
   */
  // @Output() selectedObjectChange = new EventEmitter<T | null>();
  @Output() selectedObjectChange = new EventEmitter<T | null>();



  /** Liste enrichie avec propriétés d’affichage */
  public lstDatasWithDisplay: (T & { _$display: string; _$cboDisplay: string })[] = [];

  private subscriptions: Subscription[] = [];

  constructor(public sharedSvc: SharedDataService) { }

  
  
  private _valueId?: number = undefined;
  get valueId(): number | undefined {
    return this._valueId;
  }
  // set valueId(value:number | undefined) {
  //   if (value!=this.valueId){
  //     this.writeValue( this.lstDatasWithDisplay.find(t=>t.Id == value) ?? null);
  //     this.onChange(this.value);
  //     this.onTouched();
  //   }
  // }

  set valueId(value: number | undefined) {
  if (value !== this.valueId) {
    const obj = this.lstDatasWithDisplay.find(t => t.Id == value) ?? null;

    if (this.bindToId) {
      // CVA renvoie l'ID
      this.writeValue(value ?? null);
      this.onChange(value ?? null);
    } else {
      // CVA renvoie l'objet
      this.writeValue(obj);
      this.onChange(obj);
    }

    this.onTouched();
  }
}

  // get value(): T | null {
  //   return this.lstDatasWithDisplay.find(t => t.Id == this.valueId) ?? null;
  // }

  private get _selectedObject(): T | null {
    return this.lstDatasWithDisplay.find(t => t.Id == this.valueId) ?? null;
  }
  get value(): T | number | null {
    if (this.bindToId) {
      return this.valueId ?? null;
    } else {
      return this._selectedObject;
    }
  }




  ngOnInit(): void {
    this.buildListWhenLoaded();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['data']) {
      this.buildListWhenLoaded();
    }
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach(sub => sub.unsubscribe());
  }

  private buildListWhenLoaded(): void {
    if (this.sharedSvc.allListsLoaded) {
      this.buildDisplayValues();
    } else {
      this.subscriptions.push(
        this.sharedSvc.allListsLoadedChange.subscribe(() => {
          this.buildDisplayValues();
        })
      );
    }
  }

  private buildDisplayValues(): void {
    if (!this.data || this.data.length==0) {
      this.lstDatasWithDisplay = [];
      return;
    }
    const fnDisplay =
      this.displayFn ??
      ((item: any) =>
        item._display ??
        item._cbodisplay ??
        (item.LblCod ? this.sharedSvc.getLoadedLabel(item.LblCod) : ''));
    const fnCboDisplay =
      this.displayFn ??
      ((item: any) =>
        item._cbodisplay ??
        item._display ??
        (item.LblCod ? this.sharedSvc.getLoadedLabel(item.LblCod) : ''));
    this.lstDatasWithDisplay = this.data.map(x => ({
      ...x,
      _$display: fnDisplay(x),
      _$cboDisplay: fnCboDisplay(x)
    }));
    const match = this.lstDatasWithDisplay.find(x => x.Id === this.valueId);
    this.writeValue(match);
    this.onChange(this.value); // notify Angular
  }

  // writeValue(value: T | null): void {
  //   if (value?.Id != this._valueId) {
  //     this._valueId = value?.Id || undefined;
  //     this.selectedObjectChange.emit(this.value);
  //   }
  // }

  writeValue(value: T | number | null): void {

    if (this.bindToId) {
      // value est un ID (number)
      const idValue = typeof value === 'number' ? value : null;
      this._valueId = idValue ?? undefined;

      // onChange doit renvoyer un number
      this.onChange(this._valueId ?? null);
      this.selectedObjectChange.emit(this._selectedObject);
      return;
    }

    // Sinon : mode objet (comportement actuel)
    const obj = value as T | null;
    if (obj?.Id != this._valueId) {
      this._valueId = obj?.Id ?? undefined;
      this.onChange(obj);
      this.selectedObjectChange.emit(obj);
    }
  }



  private onChange = (val: T | number | null) => { };
  private onTouched = () => { };

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  // ========= Validator =========

  validate(_: AbstractControl): ValidationErrors | null {
    if (this.required && (!this.value)) {
      return { required: true };
    }
    return null;
  }
}
